import { useBrandDna, useUpdateBrandDna } from '@studio/api-client';
import { type BrandDnaPatch, NOTES_MAX_LENGTH } from '@studio/contract';
import { Button, Input, SidePanelModal, Textarea } from '@studio/ui';
import { type FormEvent, useEffect, useMemo, useState } from 'react';

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

function parsePaletteSwatches(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => HEX_PATTERN.test(s))
    .map((s) => (s.startsWith('#') ? s : `#${s}`));
}

export interface BrandDnaModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

interface FormState {
  formality: number;
  density: number;
  threeWords: string;
  palette: string;
  moodBoard: string;
  description: string;
  aspirations: string;
  bannedWords: string;
  bannedColours: string;
  bannedStyles: string;
  notes: string;
}

const EMPTY: FormState = {
  formality: 5,
  density: 5,
  threeWords: '',
  palette: '',
  moodBoard: '',
  description: '',
  aspirations: '',
  bannedWords: '',
  bannedColours: '',
  bannedStyles: '',
  notes: '',
};

function listToText(items: string[]): string {
  return items.join(', ');
}

function textToList(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Brand DNA editor — slides in from the right (SidePanelModal). v0 form is
 * deliberately simple: comma-separated chips and number inputs. Section
 * sliders, palette swatches, and AI-assist popover land in a follow-up.
 *
 * Wire shape lives in `@studio/contract` / BrandDnaPatchSchema; the backend
 * validates with the same schema in `apps/backend/src/routes/brand-dna.ts`.
 */
export function BrandDnaModal({ open, onOpenChange }: BrandDnaModalProps) {
  const dna = useBrandDna();
  const update = useUpdateBrandDna();
  const [form, setForm] = useState<FormState>(EMPTY);

  // Hydrate the form whenever the modal opens or the server payload arrives.
  useEffect(() => {
    if (!open || !dna.data) return;
    setForm({
      formality: dna.data.voice.formality,
      density: dna.data.voice.density,
      threeWords: listToText(dna.data.voice.threeWords),
      palette: listToText(dna.data.visualMood.palette),
      moodBoard: listToText(dna.data.visualMood.moodBoard),
      description: dna.data.customer.description,
      aspirations: listToText(dna.data.customer.aspirations),
      bannedWords: listToText(dna.data.dontDo.bannedWords),
      bannedColours: listToText(dna.data.dontDo.bannedColours),
      bannedStyles: listToText(dna.data.dontDo.bannedStyles),
      notes: dna.data.notes,
    });
  }, [open, dna.data]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((s) => ({ ...s, [key]: value }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const patch: BrandDnaPatch = {
      voice: {
        formality: clamp(form.formality, 0, 10),
        density: clamp(form.density, 0, 10),
        threeWords: textToList(form.threeWords).slice(0, 5),
      },
      visualMood: {
        palette: textToList(form.palette).slice(0, 5),
        moodBoard: textToList(form.moodBoard).slice(0, 5),
      },
      customer: {
        description: form.description.slice(0, 240),
        aspirations: textToList(form.aspirations).slice(0, 5),
      },
      dontDo: {
        bannedWords: textToList(form.bannedWords).slice(0, 8),
        bannedColours: textToList(form.bannedColours).slice(0, 8),
        bannedStyles: textToList(form.bannedStyles).slice(0, 8),
      },
      notes: form.notes.slice(0, NOTES_MAX_LENGTH),
    };
    await update.mutateAsync(patch);
    onOpenChange(false);
  }

  return (
    <SidePanelModal
      open={open}
      onOpenChange={onOpenChange}
      ariaLabel="Brand DNA"
      width="wide"
      header={
        <div className="flex flex-col gap-1">
          <h2 className="display text-display-md text-fg">Brand DNA</h2>
          <p className="text-xs text-fg-3">
            Voice, mood, customer, and don't-do guidelines that the studio leans on.
          </p>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-6 px-5 py-5 text-sm">
        <Section
          title="Voice"
          subtitle="Formality: 0 = formal, 10 = playful · Density: 0 = minimalist, 10 = maximalist"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <RangeField
              label="Formality"
              value={form.formality}
              onChange={(v) => set('formality', v)}
            />
            <RangeField label="Density" value={form.density} onChange={(v) => set('density', v)} />
          </div>
          <Field label="Three words" hint="Comma-separated, up to 5">
            <Input
              value={form.threeWords}
              onChange={(e) => set('threeWords', e.currentTarget.value)}
              placeholder="warm, handmade, honest"
            />
          </Field>
        </Section>

        <Section title="Visual mood">
          <Field label="Palette" hint="Hex colors, comma-separated, up to 5">
            <Input
              value={form.palette}
              onChange={(e) => set('palette', e.currentTarget.value)}
              placeholder="#1f2933, #f4d35e, #ee964b"
            />
            <PaletteSwatches raw={form.palette} />
          </Field>
          <Field label="Mood board" hint="Free-text descriptors, comma-separated">
            <Input
              value={form.moodBoard}
              onChange={(e) => set('moodBoard', e.currentTarget.value)}
              placeholder="weekend kitchen, paper textures"
            />
          </Field>
        </Section>

        <Section title="Customer">
          <Field label="Description" hint="≤ 240 chars">
            <Textarea
              value={form.description}
              onChange={(e) => set('description', e.currentTarget.value)}
              maxLength={240}
              placeholder="Buyers picking gifts they will not be embarrassed to give."
            />
          </Field>
          <Field label="Aspirations" hint="Comma-separated, up to 5">
            <Input
              value={form.aspirations}
              onChange={(e) => set('aspirations', e.currentTarget.value)}
              placeholder="gift-worthy, thoughtful, memorable"
            />
          </Field>
        </Section>

        <Section title="Don't-do" subtitle="Lists the studio avoids, up to 8 each">
          <Field label="Banned words">
            <Input
              value={form.bannedWords}
              onChange={(e) => set('bannedWords', e.currentTarget.value)}
              placeholder="hustle, crushing it"
            />
          </Field>
          <Field label="Banned colours">
            <Input
              value={form.bannedColours}
              onChange={(e) => set('bannedColours', e.currentTarget.value)}
              placeholder="#39ff14"
            />
          </Field>
          <Field label="Banned styles">
            <Input
              value={form.bannedStyles}
              onChange={(e) => set('bannedStyles', e.currentTarget.value)}
              placeholder="stock-photo"
            />
          </Field>
        </Section>

        <Section title="Notes">
          <Field label="" hint={`Free text · ≤ ${NOTES_MAX_LENGTH} chars`}>
            <Textarea
              value={form.notes}
              onChange={(e) => set('notes', e.currentTarget.value)}
              maxLength={NOTES_MAX_LENGTH}
              placeholder="Anything else the studio should remember."
            />
          </Field>
        </Section>

        {update.error ? (
          <p className="text-sm text-danger">Save failed: {(update.error as Error).message}</p>
        ) : null}

        <div className="sticky bottom-0 -mx-5 -mb-5 flex items-center justify-end gap-3 border-t border-border-subtle bg-bg/95 px-5 py-3 backdrop-blur">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save brand DNA'}
          </Button>
        </div>
      </form>
    </SidePanelModal>
  );
}

function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div>
        <h3 className="text-md font-medium text-fg">{title}</h3>
        {subtitle ? <p className="mt-0.5 text-xs text-fg-3">{subtitle}</p> : null}
      </div>
      <div className="flex flex-col gap-3">{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {label ? <span className="text-xs font-medium text-fg-2">{label}</span> : null}
      {children}
      {hint ? <span className="text-[10px] text-fg-3">{hint}</span> : null}
    </div>
  );
}

function RangeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (next: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-medium text-fg-2">{label}</span>
        <span className="text-xs tabular-nums text-fg-3">{value} / 10</span>
      </div>
      <input
        type="range"
        min={0}
        max={10}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.currentTarget.value))}
        aria-label={label}
        className="h-1.5 w-full cursor-pointer appearance-none rounded-pill bg-surface-2 accent-brand"
      />
    </div>
  );
}

function PaletteSwatches({ raw }: { raw: string }) {
  const swatches = useMemo(() => parsePaletteSwatches(raw), [raw]);
  if (swatches.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1.5" aria-label="Palette preview">
      {swatches.map((hex) => (
        <span
          key={hex}
          className="h-5 w-5 rounded-md border border-border-subtle"
          style={{ backgroundColor: hex }}
          title={hex}
        />
      ))}
    </div>
  );
}
